import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'

const ACCESS_TOKEN_KEY = 'crm_access_token'
const REFRESH_TOKEN_KEY = 'crm_refresh_token'

export { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY }

// ===== CASE CONVERSION HELPERS =====
function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)
}

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

function deepToSnakeCase(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(deepToSnakeCase)
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
        toSnakeCase(k),
        deepToSnakeCase(v),
      ])
    )
  }
  return obj
}

function deepToCamelCase(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(deepToCamelCase)
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
        toCamelCase(k),
        deepToCamelCase(v),
      ])
    )
  }
  return obj
}

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb)
}

// Create the main axios instance
const apiClient = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
  withCredentials: true,   // send httpOnly auth cookies automatically
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// ===== REQUEST INTERCEPTOR =====
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY)
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Attach tenant ID if available
    const tenantId = localStorage.getItem('crm_tenant_id')
    if (tenantId && config.headers) {
      config.headers['X-Tenant-ID'] = tenantId
    }

    // Convert outgoing JSON body from camelCase → snake_case
    if (config.data && !(config.data instanceof FormData)) {
      config.data = deepToSnakeCase(config.data)
    }

    // Convert query params from camelCase → snake_case
    if (config.params && typeof config.params === 'object') {
      config.params = deepToSnakeCase(config.params)
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// ===== RESPONSE INTERCEPTOR =====
apiClient.interceptors.response.use(
  (response) => {
    // Skip processing for binary responses (blob/arraybuffer) — e.g. Excel exports
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      return response
    }
    // Unwrap the API response envelope if present, then convert snake_case → camelCase
    const raw = response.data
    if (raw && typeof raw === 'object' && 'success' in raw) {
      const rawObj = raw as Record<string, unknown>
      // Paginated response: merge items array + meta into one object so hooks can read .data, .total, .totalPages etc.
      if (rawObj.meta && typeof rawObj.meta === 'object') {
        return { ...response, data: deepToCamelCase({ data: rawObj.data, ...(rawObj.meta as object) }) }
      }
      return { ...response, data: deepToCamelCase(rawObj.data) }
    }
    return { ...response, data: deepToCamelCase(raw) }
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    if (!error.response) {
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        statusCode: 0,
        code: 'NETWORK_ERROR',
      })
    }

    const status = error.response.status

    // Handle 401 - try token refresh via httpOnly cookie (cookie is sent automatically)
    // Skip refresh for auth endpoints to avoid masking the real error (e.g. "wrong password")
    const reqUrl = originalRequest.url || ''
    const isAuthEndpoint = reqUrl.includes('/auth/login') || reqUrl.includes('/auth/refresh') || reqUrl.includes('/auth/logout') || reqUrl.includes('/auth/forgot') || reqUrl.includes('/auth/reset')
    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Wait for the current refresh to complete
        return new Promise<string>((resolve) => {
          addRefreshSubscriber((token: string) => {
            resolve(token)
          })
        }).then((token) => {
          if (originalRequest.headers) {
            (originalRequest.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
          }
          return apiClient(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Cookies are sent automatically via withCredentials; no body token needed
        const response = await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true })

        const raw = response.data?.data || response.data
        const accessToken: string = raw.access_token || raw.accessToken

        // Keep localStorage in sync for legacy reads elsewhere in the app
        if (accessToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
          const newRefresh: string | undefined = raw.refresh_token || raw.refreshToken
          if (newRefresh) localStorage.setItem(REFRESH_TOKEN_KEY, newRefresh)
        }

        onRefreshed(accessToken)
        isRefreshing = false

        if (originalRequest.headers) {
          (originalRequest.headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`
        }
        return apiClient(originalRequest)
      } catch (refreshError) {
        isRefreshing = false
        refreshSubscribers = []
        clearAuthTokens()
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        return Promise.reject(normalizeError(refreshError as AxiosError))
      }
    }

    // Handle 403 - Forbidden
    if (status === 403) {
      return Promise.reject({
        message: 'You do not have permission to perform this action.',
        statusCode: 403,
        code: 'FORBIDDEN',
      })
    }

    // Handle 404
    if (status === 404) {
      return Promise.reject({
        message: 'The requested resource was not found.',
        statusCode: 404,
        code: 'NOT_FOUND',
      })
    }

    // Handle 422 - Validation errors
    if (status === 422) {
      const responseData = error.response.data as Record<string, unknown>
      return Promise.reject({
        message: (responseData?.message as string) || 'Validation failed.',
        statusCode: 422,
        errors: responseData?.errors || {},
        code: 'VALIDATION_ERROR',
      })
    }

    // Handle 500+ server errors
    if (status >= 500) {
      return Promise.reject({
        message: 'Server error. Please try again later.',
        statusCode: status,
        code: 'SERVER_ERROR',
      })
    }

    return Promise.reject(normalizeError(error))
  }
)

function normalizeError(error: AxiosError | unknown) {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data as Record<string, unknown> | undefined
    return {
      message:
        (responseData?.detail as string) ||   // FastAPI HTTPException (e.g. duplicate-record 400)
        (responseData?.message as string) ||
        (responseData?.error as string) ||
        error.message ||
        'An unexpected error occurred.',
      statusCode: error.response?.status || 0,
      errors: responseData?.errors || {},
      code: (responseData?.code as string) || 'UNKNOWN_ERROR',
    }
  }
  return {
    message: 'An unexpected error occurred.',
    statusCode: 0,
    code: 'UNKNOWN_ERROR',
  }
}

export function clearAuthTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem('crm_tenant_id')
}

export function setAuthTokens(accessToken: string, refreshToken: string, tenantId?: string) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  if (tenantId) {
    localStorage.setItem('crm_tenant_id', tenantId)
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export default apiClient
